import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Megaphone, ExternalLink, Volume2, VolumeX, Mail, Phone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Analytics tracking helpers (session-scoped, deduplicated) ── */
const _getSessionId = () => {
  let sid = sessionStorage.getItem('med_sid');
  if (!sid) { sid = crypto.randomUUID(); sessionStorage.setItem('med_sid', sid); }
  return sid;
};
const _impressionKeys = new Set();
const _trackImpression = (sponsorId, sponsorName) => {
  const key = `sp_imp_${sponsorId}`;
  if (_impressionKeys.has(key) || sessionStorage.getItem(key)) return;
  _impressionKeys.add(key);
  sessionStorage.setItem(key, '1');
  fetch(`${API}/api/sponsor/track/impression`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sponsor_id: sponsorId, sponsor_name: sponsorName || '', session_id: _getSessionId() }),
  }).catch(() => {});
};
const _trackClick = (sponsorId, sponsorName) => {
  fetch(`${API}/api/sponsor/track/click`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sponsor_id: sponsorId, sponsor_name: sponsorName || '', session_id: _getSessionId() }),
  }).catch(() => {});
};

/* ── Contact helper ── */
const ContactLink = ({ info }) => {
  if (!info) return null;
  const trimmed = info.trim();
  if (trimmed.includes('@')) {
    return (
      <a href={`mailto:${trimmed}`} className="text-white/30 text-xs hover:text-white/50 transition-colors inline-flex items-center gap-1.5" data-testid="sponsor-contact">
        <Mail size={11} /> {trimmed}
      </a>
    );
  }
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length >= 7) {
    return (
      <a href={`tel:${trimmed}`} className="text-white/30 text-xs hover:text-white/50 transition-colors inline-flex items-center gap-1.5" data-testid="sponsor-contact">
        <Phone size={11} /> {trimmed}
      </a>
    );
  }
  return <p className="text-white/30 text-xs" data-testid="sponsor-contact">{trimmed}</p>;
};

/* ── Helper: extract clean domain from URL ── */
const cleanDomain = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, '');
  } catch { return null; }
};

/* ── Sponsor metadata block (reused in active sponsor + default promo + admin preview) ── */
export const SponsorInfo = ({ sponsor, isDefault, onCtaClick }) => {
  if (!sponsor) return null;
  const hasName = !!sponsor.sponsor_name;
  const hasHeadline = !!sponsor.headline;
  const hasDesc = !!sponsor.description;
  const hasUrl = !!sponsor.website_url;
  const hasContact = isDefault ? !!sponsor.contact_info : (!!sponsor.contact_info && !!sponsor.display_contact_publicly);
  if (!hasName && !hasHeadline && !hasDesc && !hasUrl && !hasContact) return null;

  const domain = cleanDomain(sponsor.website_url);
  const ctaLabel = (sponsor.cta_text || '').trim() || 'VISIT WEBSITE';

  return (
    <div className="p-5 sm:p-6 space-y-3" data-testid="sponsor-info">
      {hasName && (
        <p className="text-white font-bold text-sm sm:text-base tracking-wide" data-testid="sponsor-name-display">{sponsor.sponsor_name}</p>
      )}
      {hasHeadline && (
        <p className="text-white/60 text-sm font-medium" data-testid="sponsor-headline">{sponsor.headline}</p>
      )}
      {hasDesc && (
        <p className="text-white/35 text-xs sm:text-sm leading-relaxed" data-testid="sponsor-description">{sponsor.description}</p>
      )}
      {hasUrl && (
        <div className="space-y-1.5">
          <a href={sponsor.website_url.startsWith('http') ? sponsor.website_url : `https://${sponsor.website_url}`}
            target="_blank" rel="noopener noreferrer"
            onClick={onCtaClick}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#E91E8D]/10 border border-[#E91E8D]/20 hover:bg-[#E91E8D]/20 text-[#E91E8D] text-xs font-semibold tracking-wide uppercase transition-colors"
            data-testid="sponsor-cta">
            <ExternalLink size={12} /> {ctaLabel}
          </a>
          {domain && (
            <p className="text-white/25 text-[11px]" data-testid="sponsor-domain">{domain}</p>
          )}
        </div>
      )}
      {hasContact && <ContactLink info={sponsor.contact_info} />}
    </div>
  );
};

/* ── Main component ── */
const SponsorDisplay = () => {
  const [sponsor, setSponsor] = useState(null);
  const [defaultPromo, setDefaultPromo] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const vidRef = useRef(null);
  const sectionRef = useRef(null);
  const impressionFired = useRef(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/sponsor/current`).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/sponsor/default-video`).then(r => r.json()).catch(() => null),
    ]).then(([sp, dv]) => {
      if (sp) setSponsor(sp);
      if (dv) setDefaultPromo(dv);
      setLoaded(true);
    });
  }, []);

  // Sponsor impression tracking via IntersectionObserver
  useEffect(() => {
    if (!loaded || impressionFired.current) return;
    const activeSponsor = sponsor || defaultPromo;
    if (!activeSponsor) return;
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !impressionFired.current) {
        impressionFired.current = true;
        _trackImpression(activeSponsor.id || activeSponsor.sponsor_name || 'default', activeSponsor.sponsor_name || '');
        obs.disconnect();
      }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loaded, sponsor, defaultPromo]);

  const videoRef = useCallback((node) => {
    if (!node) return;
    vidRef.current = node;
    node.loop = true;
    node.muted = true;
    node.playsInline = true;
    node.setAttribute('playsinline', '');
    node.setAttribute('webkit-playsinline', '');

    const tryPlay = () => {
      if (node.paused) node.play().catch(() => {});
    };

    node.addEventListener('loadedmetadata', tryPlay);
    node.addEventListener('loadeddata', tryPlay);
    node.addEventListener('canplay', tryPlay);
    tryPlay();

    // IntersectionObserver: restart play when video scrolls into view
    if (typeof IntersectionObserver !== 'undefined') {
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && node.paused && node.readyState >= 2) {
          node.play().catch(() => {});
        }
      }, { threshold: 0.25 });
      obs.observe(node);
    }
  }, []);

  useEffect(() => {
    const vid = vidRef.current;
    if (!vid) return;
    // Persistent polling fallback — keep retrying until video plays
    const id = setInterval(() => {
      if (vid.paused && vid.readyState >= 2) vid.play().catch(() => {});
    }, 800);
    // Also ensure loop stays on
    const handleEnded = () => { vid.currentTime = 0; vid.play().catch(() => {}); };
    vid.addEventListener('ended', handleEnded);
    return () => { clearInterval(id); vid.removeEventListener('ended', handleEnded); };
  }, [sponsor, defaultPromo]);

  const handleVideoTap = (e) => {
    const vid = e.target;
    if (vid.muted) { vid.muted = false; vid.play(); setIsMuted(false); }
    else { vid.muted = true; setIsMuted(true); }
  };

  if (!loaded) return null;

  const hasSponsor = !!sponsor;
  const hasDefaultVideo = !!defaultPromo?.video_url;
  const hasDefaultMeta = !!(defaultPromo?.sponsor_name || defaultPromo?.headline || defaultPromo?.description || defaultPromo?.website_url);
  const hasDefault = hasDefaultVideo || hasDefaultMeta;

  /* ─── No content at all → branded placeholder ─── */
  if (!hasSponsor && !hasDefault) {
    return (
      <section className="px-4 py-10" data-testid="sponsor-section">
        <div className="max-w-4xl mx-auto">
          <SectionHeader />
          <div className="rounded-2xl overflow-hidden border border-white/5 bg-white/[0.02] p-8 sm:p-12 text-center" data-testid="sponsor-placeholder">
            <Megaphone size={32} className="text-[#E91E8D]/30 mx-auto mb-4" />
            <p className="text-white/30 text-sm font-medium mb-1">Your ad could be here</p>
            <p className="text-white/15 text-xs mb-5">Feature your brand to our entire audience for a full day</p>
            <a href="/sponsor"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#E91E8D]/10 border border-[#E91E8D]/20 hover:bg-[#E91E8D]/20 text-[#E91E8D] text-xs font-semibold transition-colors"
              data-testid="sponsor-cta-placeholder">
              Sponsor This Room
            </a>
          </div>
        </div>
      </section>
    );
  }

  /* ─── Active paid sponsor ─── */
  if (hasSponsor) {
    const handleCtaClick = () => _trackClick(sponsor.id || sponsor.sponsor_name, sponsor.sponsor_name);
    return (
      <section ref={sectionRef} className="px-4 py-10" data-testid="sponsor-section">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <SectionHeader subtitle={`Presented by ${sponsor.sponsor_name}`} />

            <div className="rounded-2xl overflow-hidden border border-white/5 bg-white/[0.02]" data-testid="sponsor-card">
              {/* Media */}
              {sponsor.media_url && (
                <div className="relative">
                  {sponsor.media_type === 'video' ? (
                    <>
                      <video ref={videoRef} src={sponsor.media_url} preload="auto"
                        autoPlay loop muted playsInline
                        onClick={handleVideoTap}
                        onLoadedData={(e) => { if (e.target.paused) e.target.play().catch(() => {}); }}
                        onCanPlay={(e) => { if (e.target.paused) e.target.play().catch(() => {}); }}
                        onEnded={(e) => { e.target.currentTime = 0; e.target.play(); }}
                        className="w-full cursor-pointer" style={{ objectFit: 'contain', maxHeight: '60vh' }}
                        data-testid="sponsor-video" />
                      <MuteToggle isMuted={isMuted} onClick={(e) => { e.stopPropagation(); handleVideoTap({ target: vidRef.current }); }} />
                    </>
                  ) : (
                    <img src={sponsor.media_url} alt={sponsor.headline || sponsor.sponsor_name}
                      className="w-full" style={{ objectFit: 'contain', maxHeight: '60vh' }}
                      data-testid="sponsor-image" />
                  )}
                </div>
              )}

              {/* Sponsor metadata */}
              <SponsorInfo sponsor={sponsor} onCtaClick={handleCtaClick} />
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  /* ─── Default promo video + metadata ─── */
  const handleDefaultCtaClick = () => _trackClick(defaultPromo?.id || defaultPromo?.sponsor_name || 'default', defaultPromo?.sponsor_name || '');
  return (
    <section ref={sectionRef} className="px-4 py-10" data-testid="sponsor-section">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <SectionHeader subtitle={defaultPromo?.sponsor_name ? `Presented by ${defaultPromo.sponsor_name}` : undefined} />
          <div className="rounded-2xl overflow-hidden border border-white/5 bg-white/[0.02]" data-testid="sponsor-default-card">
            {defaultPromo.video_url && (
              <div className="relative">
                <video ref={videoRef} src={defaultPromo.video_url} preload="auto"
                  autoPlay loop muted playsInline
                  onClick={handleVideoTap}
                  onLoadedData={(e) => { if (e.target.paused) e.target.play().catch(() => {}); }}
                  onCanPlay={(e) => { if (e.target.paused) e.target.play().catch(() => {}); }}
                  onEnded={(e) => { e.target.currentTime = 0; e.target.play(); }}
                  className="w-full cursor-pointer" style={{ objectFit: 'contain', maxHeight: '60vh' }}
                  data-testid="sponsor-default-video" />
                <MuteToggle isMuted={isMuted} onClick={(e) => { e.stopPropagation(); handleVideoTap({ target: vidRef.current }); }} />
              </div>
            )}
            {/* Default promo metadata — same layout as active sponsor */}
            <SponsorInfo sponsor={defaultPromo} isDefault onCtaClick={handleDefaultCtaClick} />
            {/* Fallback CTA if no metadata configured */}
            {!defaultPromo?.sponsor_name && !defaultPromo?.headline && !defaultPromo?.description && !defaultPromo?.website_url && (
              <div className="p-5 sm:p-6 text-center">
                <a href="/sponsor"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#E91E8D]/10 border border-[#E91E8D]/20 hover:bg-[#E91E8D]/20 text-[#E91E8D] text-xs font-semibold tracking-wide uppercase transition-colors"
                  data-testid="sponsor-cta-default">
                  Sponsor This Room
                </a>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ── Shared small components ── */
const SectionHeader = ({ subtitle }) => (
  <div className="text-center mb-6">
    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E91E8D]/10 border border-[#E91E8D]/20 mb-3">
      <Megaphone size={12} className="text-[#E91E8D]" />
      <span className="text-[#E91E8D] text-[10px] font-bold tracking-widest uppercase">Sponsor of the Day</span>
    </div>
    {subtitle && <p className="text-white/20 text-xs">{subtitle}</p>}
  </div>
);

const MuteToggle = ({ isMuted, onClick }) => (
  <button onClick={onClick}
    className="absolute bottom-3 right-3 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm text-white/70 text-[10px] font-medium flex items-center gap-1.5 z-10"
    data-testid="sponsor-mute-toggle">
    {isMuted ? <VolumeX size={12} /> : <Volume2 size={12} />}
    {isMuted ? 'Tap for sound' : 'Mute'}
  </button>
);

export default SponsorDisplay;
